import { SCREEN_FADE_MS } from '../config/kiosk'
import type { Screen, ScreenFactory, ScreenId } from './types'

interface Mounted {
  id: ScreenId
  screen: Screen
  el: HTMLElement
}

/**
 * Minimal screen stack: one screen visible at a time, crossfaded.
 *
 * Deliberately not a URL router — a kiosk has no address bar, and keeping
 * navigation out of `history` means a stray back-gesture can't strand a visitor
 * on a blank page.
 */
export class Router {
  private current: Mounted | undefined
  private onChange: ((id: ScreenId) => void) | undefined

  constructor(
    private readonly container: HTMLElement,
    private readonly routes: Record<ScreenId, ScreenFactory>,
  ) {}

  /** Notified after every successful navigation. */
  subscribe(fn: (id: ScreenId) => void): void {
    this.onChange = fn
  }

  get currentId(): ScreenId | undefined {
    return this.current?.id
  }

  go(id: ScreenId, param?: string): void {
    if (this.current?.id === id) return

    const factory = this.routes[id]
    const screen = factory()

    const el = document.createElement('div')
    el.className = 'screen'
    el.dataset.screen = id

    screen.mount(el, {
      go: (next, nextParam) => this.go(next, nextParam),
      param,
    })

    this.container.appendChild(el)
    // Force layout so the browser registers the pre-transition state.
    void el.offsetHeight
    el.classList.add('is-active')

    const previous = this.current
    if (previous) {
      previous.el.classList.remove('is-active')
      window.setTimeout(() => {
        previous.screen.unmount?.()
        previous.el.remove()
      }, SCREEN_FADE_MS)
    }

    this.current = { id, screen, el }
    this.onChange?.(id)
  }
}
